import { useState, useCallback, useRef, useEffect } from 'react'
import * as TwilioVideo from 'twilio-video'
import { getTwilioToken } from '../services/videocall/twilioService'

/**
 * Custom hook for Twilio Video integration
 * @param {string} roomId - Room ID
 * @returns {Object} Twilio state and controls
 * 
 * NOTE: Twilio Real-time Transcriptions is a Public Beta feature
 * To enable:
 * 1. Log in to https://console.twilio.com
 * 2. Navigate to Video → Settings → Room Settings
 * 3. Enable "Real-time Transcriptions"
 * 4. Accept AI/ML Features Addendum
 * Until then, Web Speech API serves as fallback
 */
export function useTwilioVideo(roomId) {
  const [isConnected, setIsConnected] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const [twilioRoom, setTwilioRoom] = useState(null)
  const [localTracks, setLocalTracks] = useState([])
  const [remoteTracks, setRemoteTracks] = useState([])
  const [remoteParticipant, setRemoteParticipant] = useState(null)
  const [error, setError] = useState(null)
  const [isRecording, setIsRecording] = useState(false)
  const [transcriptionEnabled, setTranscriptionEnabled] = useState(false)
  const [twilioTranscripts, setTwilioTranscripts] = useState([])

  const twilioRoomRef = useRef(null)
  const localTracksRef = useRef([])
  const remoteTracksRef = useRef([])
  const participantMapRef = useRef(new Map()) // Map participant SID to identity

  /**
   * Connect to Twilio Video room
   */
  const connectToRoom = useCallback(async (isInterviewer, identityData = {}) => {
    // Prevent multiple simultaneous connection attempts
    if (isConnecting) {
      return
    }

    // If already connected, don't connect again
    if (isConnected) {
      return
    }

    setIsConnecting(true)
    setError(null)

    try {
      // Disconnect any existing connection first to prevent duplicates
      if (twilioRoomRef.current) {
        try {
          await twilioRoomRef.current.disconnect()
        } catch (disconnectError) {
          console.warn('[Twilio Video Hook] Error disconnecting existing room:', disconnectError.message)
        }
        twilioRoomRef.current = null
      }

      // Get access token from server
      const response = await getTwilioToken(roomId, isInterviewer, identityData)

      if (!response.success) {
        // Create an error object that includes the full response for proper error handling
        const error = new Error(response.error || 'Failed to get access token')
        error.response = { data: response }
        throw error
      }

      const { token, room, identity } = response

      // Create local tracks
      const tracks = await TwilioVideo.createLocalTracks({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        },
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          frameRate: { ideal: 30 }
        }
      })

      localTracksRef.current = tracks
      setLocalTracks(tracks)

      // Connect to Twilio room
      const twilioRoomInstance = await TwilioVideo.connect(token, {
        name: room.name,
        tracks: tracks,
        dominantSpeaker: true,
        networkQuality: {
          local: 1,
          remote: 1
        },
        receiveTranscriptions: true // Enable transcription events
      })

      twilioRoomRef.current = twilioRoomInstance
      setTwilioRoom(twilioRoomInstance)

      // Store local participant SID to identity mapping
      if (twilioRoomInstance.localParticipant) {
        participantMapRef.current.set(twilioRoomInstance.localParticipant.sid, twilioRoomInstance.localParticipant.identity)
      }

      // Set up room event listeners
      setupRoomEventListeners(twilioRoomInstance)

      setIsConnected(true)
      setIsRecording(true) // Recording starts automatically with Twilio
      setTranscriptionEnabled(true) // Transcription is configured

      return twilioRoomInstance
    } catch (err) {
      console.error('[Twilio Video Hook] Error connecting:', err)
      setError(err.message)
      throw err
    } finally {
      setIsConnecting(false)
    }
  }, [roomId])

  /**
   * Handle transcription events from Twilio
   */
  const handleTranscriptionEvent = useCallback((transcriptionEvent) => {
    try {
      // Parse the transcription event - it might be a JSON string or already an object
      let transcriptionData
      if (typeof transcriptionEvent === 'string') {
        transcriptionData = JSON.parse(transcriptionEvent)
      } else {
        transcriptionData = transcriptionEvent
      }

      // Extract fields according to Twilio's API specification
      const {
        type,
        version,
        participant: participantSid,
        track: trackSid,
        transcription,
        partial_results,
        stability,
        language_code,
        sequence_number,
        timestamp
      } = transcriptionData

      // Validate that this is a transcription event
      if (type !== 'extension_transcriptions') {
        console.warn('[Twilio Video Hook] Received non-transcription event:', type)
        return
      }

      // Map participant SID to identity
      const participantIdentity = participantMapRef.current.get(participantSid)
      if (!participantIdentity) {
        console.warn('[Twilio Video Hook] Unknown participant SID:', participantSid, 'Available mappings:', Array.from(participantMapRef.current.entries()))
        return
      }

      // Only process final results to avoid duplicates from accumulated partial results
      if (!partial_results) {
        const transcriptData = {
          participant: participantIdentity, // Use mapped identity instead of SID
          participantSid, // Keep SID for reference
          transcription,
          timestamp: new Date(timestamp).getTime(),
          partial_results,
          stability,
          language_code,
          sequence_number,
          trackSid,
          source: 'twilio',
          version
        }

        setTwilioTranscripts(prev => [...prev, transcriptData])
        setTranscriptionEnabled(true)
      }
    } catch (error) {
      console.error('[Twilio Video Hook] ❌ Error handling transcription event:', error)
      console.error('[Twilio Video Hook] Raw event that caused error:', transcriptionEvent)
    }
  }, [])

  /**
   * Set up room event listeners
   */
  const setupRoomEventListeners = useCallback((room) => {
    // Participant connected
    room.on('participantConnected', (participant) => {
      // Store participant SID to identity mapping
      participantMapRef.current.set(participant.sid, participant.identity)
      setRemoteParticipant(participant)
      handleRemoteParticipant(participant)
    })

    // Participant disconnected
    room.on('participantDisconnected', (participant) => {
      setRemoteParticipant(null)
      setRemoteTracks([])
      remoteTracksRef.current = []
    })

    // Room disconnected
    room.on('disconnected', (room, error) => {
      setIsConnected(false)
      setIsRecording(false)
      setTwilioRoom(null)
      setRemoteParticipant(null)
      setRemoteTracks([])
      remoteTracksRef.current = []
    })

    // Track published by remote participant
    room.on('trackPublished', (publication, participant) => {
      if (participant !== room.localParticipant) {
        handleRemoteTrack(publication.track)
      }
    })

    // Track unpublished by remote participant
    room.on('trackUnpublished', (publication, participant) => {
      if (participant !== room.localParticipant) {
        removeRemoteTrack(publication.track)
      }
    })

    // Transcription events
    room.on('transcription', (transcriptionEvent) => {
      handleTranscriptionEvent(transcriptionEvent)
    })

    // Also listen for any other events that might be transcription-related
    room.on('trackSubscribed', (track, publication, participant) => {
      // Track subscribed
    })

    room.on('trackUnsubscribed', (track, publication, participant) => {
      // Track unsubscribed
    })
  }, [handleTranscriptionEvent])

  /**
   * Handle remote participant tracks
   */
  const handleRemoteParticipant = useCallback((participant) => {
    // Subscribe to existing tracks
    participant.tracks.forEach((publication) => {
      if (publication.track) {
        handleRemoteTrack(publication.track)
      }
    })

    // Listen for new tracks
    participant.on('trackSubscribed', (track) => {
      handleRemoteTrack(track)
    })

    participant.on('trackUnsubscribed', (track) => {
      removeRemoteTrack(track)
    })
  }, [])

  /**
   * Handle remote track
   */
  const handleRemoteTrack = useCallback((track) => {
    setRemoteTracks(prev => {
      const newTracks = [...prev, track]
      remoteTracksRef.current = newTracks
      return newTracks
    })
  }, [])

  /**
   * Remove remote track
   */
  const removeRemoteTrack = useCallback((track) => {
    setRemoteTracks(prev => {
      const newTracks = prev.filter(t => t !== track)
      remoteTracksRef.current = newTracks
      return newTracks
    })
  }, [])

  /**
   * Toggle local video
   */
  const toggleVideo = useCallback(() => {
    const videoTrack = localTracksRef.current.find(track => track.kind === 'video')
    if (videoTrack) {
      videoTrack.enable(!videoTrack.isEnabled)
    }
  }, [])

  /**
   * Toggle local audio
   */
  const toggleAudio = useCallback(() => {
    const audioTrack = localTracksRef.current.find(track => track.kind === 'audio')
    if (audioTrack) {
      audioTrack.enable(!audioTrack.isEnabled)
    }
  }, [])

  /**
   * Check if video is enabled
   */
  const isVideoEnabled = useCallback(() => {
    const videoTrack = localTracksRef.current.find(track => track.kind === 'video')
    return videoTrack ? videoTrack.isEnabled : false
  }, [])

  /**
   * Check if audio is enabled
   */
  const isAudioEnabled = useCallback(() => {
    const audioTrack = localTracksRef.current.find(track => track.kind === 'audio')
    return audioTrack ? audioTrack.isEnabled : false
  }, [])

  /**
   * Disconnect from room
   */
  const disconnectFromRoom = useCallback(() => {
    try {
      // Stop local tracks
      localTracksRef.current.forEach(track => {
        track.stop()
      })

      // Disconnect from room
      if (twilioRoomRef.current) {
        twilioRoomRef.current.disconnect()
        twilioRoomRef.current = null
      }

      // Clear state
      setIsConnected(false)
      setIsRecording(false)
      setTwilioRoom(null)
      setRemoteParticipant(null)
      setLocalTracks([])
      setRemoteTracks([])
      setTranscriptionEnabled(false)
      setTwilioTranscripts([])
      localTracksRef.current = []
      remoteTracksRef.current = []
      participantMapRef.current.clear()
    } catch (err) {
      console.error('[Twilio Video Hook] Error disconnecting:', err)
    }
  }, [])

  /**
   * Get local video track
   */
  const getLocalVideoTrack = useCallback(() => {
    return localTracksRef.current.find(track => track.kind === 'video')
  }, [])

  /**
   * Get local audio track
   */
  const getLocalAudioTrack = useCallback(() => {
    return localTracksRef.current.find(track => track.kind === 'audio')
  }, [])

  /**
   * Get remote video track
   */
  const getRemoteVideoTrack = useCallback(() => {
    return remoteTracksRef.current.find(track => track.kind === 'video')
  }, [])

  /**
   * Get remote audio track
   */
  const getRemoteAudioTrack = useCallback(() => {
    return remoteTracksRef.current.find(track => track.kind === 'audio')
  }, [])

  /**
   * Cleanup on unmount and handle page refresh
   */
  useEffect(() => {
    // Cleanup function for component unmount
    const cleanup = () => {
      disconnectFromRoom()
    }
    
    // Handle page refresh/close
    const handleBeforeUnload = () => {
      if (twilioRoomRef.current) {
        // Force disconnect on page unload
        twilioRoomRef.current.disconnect()
      }
    }
    
    // Add event listener for page unload
    window.addEventListener('beforeunload', handleBeforeUnload)
    
    return () => {
      cleanup()
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [disconnectFromRoom])

  return {
    // Connection state
    isConnected,
    isConnecting,
    error,
    isRecording,

    // Room and participants
    twilioRoom,
    remoteParticipant,

    // Tracks
    localTracks,
    remoteTracks,

    // Transcription
    transcriptionEnabled,
    twilioTranscripts,

    // Actions
    connectToRoom,
    disconnectFromRoom,
    toggleVideo,
    toggleAudio,

    // Track getters
    getLocalVideoTrack,
    getLocalAudioTrack,
    getRemoteVideoTrack,
    getRemoteAudioTrack,

    // State checkers
    isVideoEnabled,
    isAudioEnabled,
  }
}
